<mat-toolbar class="ui-topbar">
  <nav class="ui-topbar__breadcrumb" aria-label="Breadcrumb">
    @if (breadcrumbs().length > 1) {
      <button type="button" class="ui-topbar__back" aria-label="Go back to previous page" (click)="onBackClicked()">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20z" />
        </svg>
      </button>
    }
    @for (crumb of breadcrumbs(); track crumb.label; let last = $last) {
      @if (crumb.path && !last) {
        <a class="ui-topbar__crumb ui-topbar__crumb--link" [routerLink]="crumb.path">{{ crumb.label | uiLabelTranslate }}</a>
      } @else {
        <span class="ui-topbar__crumb ui-topbar__crumb--active">{{ crumb.label | uiLabelTranslate }}</span>
      }
      @if (!last) {
        <span class="ui-topbar__crumb-sep" aria-hidden="true">/</span>
      }
    }
  </nav>

  <div class="ui-topbar__actions">
    <button class="icon-button" type="button" [attr.aria-label]="'topbar.notifications' | translate" (click)="toggleNotifications()">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2Zm6-6V11a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2Z" />
      </svg>
      @if (unreadNotificationCount() > 0) {
        <span class="badge-count">{{ unreadNotificationCount() }}</span>
      }
    </button>
    <span class="divider"></span>
    <div class="user-menu" (click)="$event.stopPropagation()">
      <button class="user-chip" type="button" (click)="toggleMenu()" aria-label="User profile menu" [attr.aria-expanded]="menuOpen()">
        <span class="avatar">{{ user().initials }}</span>
        <span class="name">{{ user().name }}</span>
        <svg class="chevron" [class.chevron--open]="menuOpen()" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m7 10 5 5 5-5z" />
        </svg>
      </button>

      @if (menuOpen()) {
        <section class="profile-dropdown" role="menu" aria-label="Profile menu">
          <button class="dropdown-item" type="button" role="menuitem" (click)="onProfileClicked()">{{ 'topbar.viewProfile' | translate }}</button>
          <div class="dropdown-language">
            <ui-select
              [label]="'topbar.language' | translate"
              [value]="user().language ?? 'en'"
              [options]="languageOptions"
              (valueChange)="onLanguageChange($event)"
            ></ui-select>
          </div>
          <button class="dropdown-item dropdown-item--danger" type="button" role="menuitem" (click)="onLogoutClicked()">{{ 'topbar.logout' | translate }}</button>
        </section>
      }
    </div>
  </div>
</mat-toolbar>

<section class="ui-topbar-drawer-layer" [class.ui-topbar-drawer-layer--open]="notificationDrawerOpen()">
  <button type="button" class="ui-topbar-drawer-layer__backdrop" (click)="closeNotifications()"></button>
  <aside class="ui-topbar-drawer" [class.ui-topbar-drawer--open]="notificationDrawerOpen()">
    <header class="ui-topbar-drawer__header">
      <div>
        <h3>{{ 'topbar.notifications' | translate }}</h3>
        <span>{{ 'topbar.unreadCount' | translate: { count: unreadNotificationCount() } }}</span>
      </div>
      <div class="ui-topbar-drawer__actions">
        <ui-button variant="ghost" size="sm" [disabled]="!unreadNotificationCount()" (click)="markAllAsRead()">{{ 'topbar.markAllAsRead' | translate }}</ui-button>
        <ui-button variant="danger" size="sm" [disabled]="!notifications().length" (click)="clearNotifications()">{{ 'topbar.clearNotifications' | translate }}</ui-button>
      </div>
    </header>
    <div class="ui-topbar-drawer__tabs" role="tablist" aria-label="Notification filters">
      <button
        type="button"
        role="tab"
        [class.ui-topbar-drawer__tab--active]="activeNotificationTab() === 'all'"
        [attr.aria-selected]="activeNotificationTab() === 'all'"
        (click)="setNotificationTab('all')"
      >
        {{ 'topbar.allNotifications' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        [class.ui-topbar-drawer__tab--active]="activeNotificationTab() === 'unread'"
        [attr.aria-selected]="activeNotificationTab() === 'unread'"
        (click)="setNotificationTab('unread')"
      >
        {{ 'topbar.unreadNotifications' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        [class.ui-topbar-drawer__tab--active]="activeNotificationTab() === 'read'"
        [attr.aria-selected]="activeNotificationTab() === 'read'"
        (click)="setNotificationTab('read')"
      >
        {{ 'topbar.readNotifications' | translate }}
      </button>
    </div>
    <div class="ui-topbar-drawer__body">
      @if (!filteredNotifications().length) {
        <div class="ui-topbar-empty">
          <strong>{{ 'topbar.noNotifications' | translate }}</strong>
          <span>{{ 'topbar.noNotificationsHint' | translate }}</span>
        </div>
      }
      @for (item of visibleNotifications(); track item.id) {
        <ui-card
          class="ui-topbar-notification"
          [class.ui-topbar-notification--read]="item.read"
          [class.ui-topbar-notification--unread]="!item.read"
          [attr.data-type]="item.type"
        >
          <button
            type="button"
            class="ui-topbar-notification__summary"
            [attr.aria-expanded]="item.expanded"
            (click)="toggleNotification(item.id)"
          >
            <span class="ui-topbar-notification__marker" aria-hidden="true"></span>
            <span class="ui-topbar-notification__copy">
              <strong>{{ item.title | uiLabelTranslate }}</strong>
            </span>
            <svg class="ui-topbar-notification__chevron" [class.ui-topbar-notification__chevron--open]="item.expanded" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m7 10 5 5 5-5z" />
            </svg>
          </button>
          @if (item.expanded) {
            <div class="ui-topbar-notification__details">
              <small>{{ item.meta | uiLabelTranslate }}</small>
              <p>{{ item.description | uiLabelTranslate }}</p>
            </div>
          }
        </ui-card>
      }
      @if (hasMoreNotifications()) {
        <div class="ui-topbar-loading">
          <ui-button variant="secondary" size="sm" [ariaLabel]="'topbar.loadMore' | translate" (click)="loadMoreNotifications()">
            <span class="ui-topbar-loading__label">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 16.5 5.5 10l1.4-1.4 4.1 4.08V3h2v9.68l4.1-4.08 1.4 1.4L12 16.5ZM5 21v-2h14v2H5Z" />
              </svg>
              {{ 'topbar.loadMore' | translate }}
            </span>
          </ui-button>
        </div>
      }
    </div>
  </aside>
</section>
